/**
 * Presentation-only formatting for the read-only list views. Pure functions,
 * no framework import, trivially unit-testable in isolation.
 */

// Fixed locale + UTC on purpose, not the visitor's own locale/timezone: the
// backend's timestamps are ISO-8601 UTC and this is an audit-adjacent admin
// view — every operator should read the same wall-clock moment for the same
// row, not a value that silently shifts with their machine's timezone.
const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "UTC",
});

/** Renders a schema-validated ISO-8601 timestamp for display, e.g. "1 Aug 2026, 10:12". */
export function formatDateTime(iso: string): string {
  return DATE_TIME_FORMATTER.format(new Date(iso));
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

/** Human-readable byte size, e.g. "180.0 KB". Zero renders as "0 B", never blank. */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) {
    return "0 B";
  }
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const unit = BYTE_UNITS[exponent] ?? "B";
  const value = bytes / 1024 ** exponent;
  return exponent === 0 ? `${value} ${unit}` : `${value.toFixed(1)} ${unit}`;
}
